import { Check, SquareArrowOutUpRight, X } from "lucide-react";
import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  IgBadge,
  IgButton,
  IgChip,
  IgDialog,
  IgIconButton,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Box, Grid, useTheme } from "@mui/material";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";
import { usePatchOrganization } from "@src/api/organizations/patch-organization";
import { organizationScriptStatusKey } from "@src/api/queryKeys";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import AppEmbedOnlyImage from "@src/images/app_embed_only.png";
import SaveChangesImage from "@src/images/save_changes_embed.png";
import ScriptOnlyImage from "@src/images/script_only_2.png";
import ScriptStepThreeImage from "@src/images/script_step_3.png";
import ToggleIgScriptImage from "@src/images/toggle_ig.png";
import { queryClient } from "@src/queryClient";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { TrackedMpClickActions } from "@src/types/mixpanel";
import { contactSupport } from "@src/utils/contactSupport";
import { FullIgScript } from "@src/utils/intelligemsThemeSnippets";
import {
  allIntegrationGuides,
  appEmbedIntegrationGuide,
  igDirectScriptAdd,
} from "@src/utils/linkSheet";
import { mpScriptIntegration } from "@src/utils/mixpanel";
import { openNewTab } from "@src/utils/openNewTab";
import { useQueryClient } from "@tanstack/react-query";

interface IgScriptTypeSelectCardProps {
  image: string;
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  isRecommended?: boolean;
}

const IgScriptTypeSelectCard = ({
  image,
  title,
  description,
  selected,
  onSelect,
  isRecommended,
}: IgScriptTypeSelectCardProps) => {
  const theme = useTheme();
  return (
    <IgPaper
      onClick={() => onSelect()}
      isClickable={true}
      isSelected={selected}
      borderColor={"primary"}
      hoverBorderColor={"accent"}
      selectedBorderColor={"accent"}
      sx={{
        width: "300px",
        padding: "0",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <Grid container direction="column" spacing={1}>
        <Grid item padding={0}>
          <Box sx={{ position: "relative", height: "164px" }}>
            <Box
              sx={{
                height: "20px",
                borderTopLeftRadius: "6px",
                borderTopRightRadius: "6px",
                backgroundColor: selected
                  ? theme.igBackgroundColors.accent
                  : theme.igBackgroundColors.baseLightLow,
              }}
            />
            <Grid item container direction={"row"} wrap={"nowrap"}>
              <Grid item>
                <Box
                  sx={{
                    width: "20px",
                    height: "100%",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    backgroundColor: selected
                      ? theme.igBackgroundColors.accent
                      : theme.igBackgroundColors.baseLightLow,
                  }}
                />
              </Grid>
              <Grid item>
                <img
                  src={image}
                  alt={title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "6px 0px",
                  }}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                position: "absolute",
                bottom: "-6px",
                left: 0,
                right: 0,
                height: "25px",
                background:
                  "linear-gradient(to top, rgba(128,128,128,0.1), rgba(128,128,128,0))",
              }}
            />

            {isRecommended && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 10,
                  top: "10px",
                  right: "8px",
                }}
              >
                <IgChip
                  size={"small"}
                  color={"success"}
                  label={"Recommended"}
                />
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                right: "8px",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: selected
                  ? theme.igBackgroundColors.accentSolid
                  : "white",
                borderRadius: "50%",
                ...(!selected && {
                  border: `1px solid ${theme.igBorders.core.default}`,
                }),
              }}
            >
              {selected && <Check size={16} color={"white"} />}
            </Box>
          </Box>
        </Grid>
        <Grid item marginLeft={5} marginTop={4}>
          <IgTypography fontWeight={600}>{title}</IgTypography>
        </Grid>
        <Grid item marginLeft={5} mb={5}>
          <IgTypography>{description}</IgTypography>
        </Grid>
      </Grid>
    </IgPaper>
  );
};

interface StepCardsContainerProps {
  steps: {
    title: string;
    description: string | ReactNode;
    button?: { text: string; onButtonClick: () => void };
    image?: string;
  }[];
}

const StepCardsContainer = ({ steps }: StepCardsContainerProps) => {
  const theme = useTheme();
  return (
    <Grid container item direction={"column"} rowSpacing={2}>
      {steps.map((step, index) => (
        <Grid item key={index}>
          <IgPaper sx={{ padding: "0", borderRadius: "6px", height: "100px" }}>
            <Grid
              item
              container
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              wrap={"nowrap"}
              sx={{
                paddingLeft: 4,
                paddingTop: step?.image ? 0 : 4,
                paddingRight: step?.image ? 0 : 4,
                paddingBottom: step?.image ? 0 : 4,
                height: "100%",
              }}
            >
              <Grid
                item
                container
                direction={"row"}
                wrap={"nowrap"}
                alignItems={"center"}
                columnSpacing={3}
                sx={{
                  paddingTop: !step?.image ? 0 : 4,
                  paddingRight: !step?.image ? 0 : 4,
                  paddingBottom: !step?.image ? 0 : 4,
                }}
              >
                <Grid item ml={4} mr={4}>
                  <IgBadge badgeContent={index + 1} color={"accent"} />
                </Grid>
                <Grid
                  item
                  container
                  direction={"column"}
                  sx={{
                    maxWidth: step?.image ? "309px" : "378px",
                    width: step?.image ? "309px" : "378px",
                  }}
                >
                  <Grid item>
                    <IgTypography variant={"h3"} fontWeight={600}>
                      {step.title}
                    </IgTypography>
                  </Grid>
                  <Grid item>
                    <IgTypography
                      variant={"body2"}
                      color={theme.palette.text.secondary}
                    >
                      {step.description}
                    </IgTypography>
                  </Grid>
                </Grid>
              </Grid>
              {/*<Grid item>*/}
              {step.button ? (
                <IgButton
                  onClick={step.button.onButtonClick}
                  endIcon={
                    step.button.text.includes("Open") ? (
                      <SquareArrowOutUpRight size={16} />
                    ) : undefined
                  }
                  sx={{
                    minWidth: step.button.text.includes("Open")
                      ? "140px"
                      : "102px",
                  }}
                  color={"secondary"}
                >
                  {step.button.text}
                </IgButton>
              ) : (
                <img
                  src={step.image}
                  alt={step.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "0px 6px 6px 0px",
                  }}
                />
              )}
              {/*</Grid>*/}
            </Grid>
          </IgPaper>
        </Grid>
      ))}
    </Grid>
  );
};

export const ScriptTagDialog = () => {
  const isOpen = useSelector(isDialogOpen("scriptTag"));
  const dispatch = useDispatch();
  const patchOrganization = usePatchOrganization();
  const org = useGetOrg();
  const organizationId = useGetOrgId();
  const scriptStatusQuery = useGetScriptStatus(org.data.org.id);

  const handleDialogClose = () => {
    dispatch(setDialog({ dialog: "scriptTag", open: false }));
  };

  const theme = useTheme();
  const onCopyClick = async () => {
    await navigator.clipboard.writeText(FullIgScript(organizationId));
    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };
  const [copyClicked, setCopyClicked] = useImmer(false);
  const queryClient = useQueryClient();

  const [step, setStep] = useImmer(0);
  const status = "failed";
  const [enableType, setEnableType] = useImmer<string>("embed");
  const [checkedScript, setCheckedScript] = useImmer(false);
  const [trackedMpAction, setTrackedMpActions] = useImmer<
    TrackedMpClickActions[]
  >([]);
  const yesButtonText = useMemo(() => {
    if (step === 0) {
      return "Continue";
    } else if (step === 1) {
      return "Test script";
    } else {
      return "Try another way";
    }
  }, [step, status]);

  const getScreenName = (step: number, enableType: string) => {
    if (step === 0) {
      return "Enable page";
    }

    if (enableType === "embed") {
      return "App embed page";
    }
    if (enableType === "script") {
      return "Script page";
    }
    return "";
  };
  const onMpTrack = (action: string) => {
    if (!trackedMpAction.includes(action as TrackedMpClickActions)) {
      const splitAction = action.split("-");

      mpScriptIntegration(
        splitAction.length > 0 ? splitAction[1] : "",
        splitAction[0]
      );
      setTrackedMpActions((draft) => {
        draft.push(action as TrackedMpClickActions);
        return draft;
      });
    }
  };

  const onCancelClick = useCallback(
    (step: number) => {
      if (step === 2) {
        onMpTrack(`Retest the script-${getScreenName(step, enableType)}`);
        void queryClient.invalidateQueries({
          queryKey: organizationScriptStatusKey.org(org.data.org.id),
        });
      } else {
        onMpTrack(`Cancel-${getScreenName(step, enableType)}`);
        handleDialogClose();
      }
    },
    [step, enableType]
  );

  const onYesClick = useCallback(
    (step: number) => {
      if (step === 0) {
        onMpTrack(
          `${enableType === "script" ? "Add script to theme path" : "App embed path"}-${getScreenName(step, enableType)}`
        );
        setStep(1);
      } else if (step === 2) {
        // try another way click
        onMpTrack(`Try another way-${getScreenName(step, enableType)}`);
        setStep(0);
      } else {
        // TEST SCRIPT
        onMpTrack(`Test script-${getScreenName(step, enableType)}`);
        setCheckedScript(true);
        void queryClient.invalidateQueries({
          queryKey: organizationScriptStatusKey.org(org.data.org.id),
        });
      }
    },
    [step]
  );

  const headerDetails = useMemo(() => {
    if (step === 0) {
      return {
        title: "Enable Intelligems",
        subtitle:
          "Choose the method based on your comfort level with adding custom code to your Shopify theme.",
      };
    }
    if (step === 1) {
      if (enableType === "script") {
        return {
          title: "Enable using the Intelligems script",
          subtitle:
            "Below are the steps you’ll need to take in Shopify to complete this process.",
        };
      } else {
        return {
          title: "Enable Intelligems using Shopify app embed",
          subtitle:
            "Below are the steps you’ll need to take in Shopify to complete this process.",
        };
      }
    }
    // failed
    if (step === 2) {
      return {
        title: "Hmm, something's not quite right",
        subtitle:
          "Unfortunately we can’t detect our script on your store theme yet. You can retest it, or try another way to enable Intelligems.",
      };
    }
    return {
      title: "",
      subtitle: "",
    };
  }, [step, enableType]);

  const screenSteps = useMemo(() => {
    if (enableType === "script") {
      return [
        {
          title: "Copy your Intelligems script",
          description:
            "Click the button here to copy your unique script to your clipboard",
          button: {
            text: copyClicked ? "Copied" : "Copy script",
            onButtonClick: () => {
              onMpTrack(`Copy script-${getScreenName(step, enableType)}`);
              void onCopyClick();
            },
          },
        },
        {
          title: "Open theme code editor in Shopify",
          description:
            "In your Shopify admin screen, navigate to Online Store → Themes → Actions → Edit code",
          button: {
            text: "Open Shopify",
            onButtonClick: () => {
              onMpTrack(`Open Shopify-${getScreenName(step, enableType)}`);
              openNewTab(
                `https://admin.shopify.com/store/${org.data.org.shopId?.replace(
                  ".myshopify.com",
                  ""
                )}`
              );
            },
          },
        },
        {
          title: "Add script to theme file(s)",
          description: (
            <IgTypography
              variant={"body2"}
              color={theme.palette.text.secondary}
            >
              Paste the Intelligems script into the &lt;head&gt; section of the
              theme.liquid file. Some stores may need more, so{" "}
              <IgTypography
                onClick={() => {
                  openNewTab(igDirectScriptAdd, "Add Script to Theme");
                }}
                variant={"body2"}
                igStyle={"linkNoBold"}
              >
                check out our docs
              </IgTypography>{" "}
              for more detail.
            </IgTypography>
          ),
          image: ScriptStepThreeImage,
        },
      ];
    } else {
      return [
        {
          title: "Open Shopify Admin",
          description:
            "Click the button to open Shopify in a new tab. Keep this window open so you can return here as needed",
          button: {
            text: "Open Shopify",
            onButtonClick: () => {
              onMpTrack(`Open Shopify-${getScreenName(step, enableType)}`);
              openNewTab(
                `${
                  org.data.org.shopId
                }/admin/themes/current/editor?context=apps&activateAppId=${
                  import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
                }/intelligems-script`,
                undefined,
                true
              );
            },
          },
        },
        {
          title: "Toggle on Intelligems Script",
          description: "It is likely already toggled on, but best to confirm.",
          image: ToggleIgScriptImage,
        },
        {
          title: "Save Changes",
          description: 'Click "Save" in the top right to apply the changes',
          image: SaveChangesImage,
        },
      ];
    }
  }, [enableType, copyClicked]);

  useEffect(() => {
    if (
      scriptStatusQuery.isSuccess &&
      !scriptStatusQuery.data.exists &&
      !scriptStatusQuery.isFetching &&
      step !== 2 &&
      checkedScript
    ) {
      setCheckedScript(false);
      setStep(2);
    } else if (
      scriptStatusQuery.isSuccess &&
      scriptStatusQuery.data.exists &&
      !scriptStatusQuery.isFetching &&
      checkedScript
    ) {
      setCheckedScript(false);
      setStep(3); // success
    }
  }, [scriptStatusQuery, checkedScript]);

  return (
    <IgDialog
      padding={"24px"}
      sx={{
        "& .MuiDialogActions-root": {
          padding: "0px !important",
          height: "36px",
        },
      }}
      yesButtonText={yesButtonText}
      shouldNotClose={true}
      hideActions={step === 3}
      learnMoreButton={
        step === 3 || step === 2 ? undefined : (
          <Grid item xs={4}>
            <IgButton
              color={"link"}
              onClick={() => {
                onMpTrack(`Learn more-${getScreenName(step, enableType)}`);
                openNewTab(
                  step === 0
                    ? allIntegrationGuides
                    : enableType === "script"
                      ? igDirectScriptAdd
                      : appEmbedIntegrationGuide,
                  "Adding Script to Theme"
                );
              }}
            >
              Learn more
            </IgButton>
          </Grid>
        )
      }
      handleNoClick={() => {
        onCancelClick(step);
      }}
      title={""}
      handleClose={() => {
        setStep(0);
        handleDialogClose();
      }}
      dontUseDialogContent={true}
      PaperProps={{
        sx: {
          width: step === 3 ? "500px" : "672px",
          maxWidth: "672px",
        },
      }}
      noButtonText={step === 2 ? "Retest the script" : "Cancel"}
      handleYesClick={() => {
        onYesClick(step);
      }}
      showDialog={isOpen}
      showCloseIcon={false}
      loading={patchOrganization.isPending || scriptStatusQuery.isFetching}
      rawContent={
        <Grid container direction={"column"} rowSpacing={6}>
          {step === 3 ? (
            <Grid
              item
              container
              direction={"column"}
              rowSpacing={1}
              textAlign={"center"}
            >
              <Grid
                item
                container
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                wrap={"nowrap"}
              >
                <Grid item />
                <Grid
                  item
                  sx={{ width: "150px", height: "150px", marginLeft: "50px" }}
                >
                  <DotLottieReact
                    src={"/lottie/script_status_check_complete.lottie"}
                    loop={true}
                    autoplay={true}
                    renderConfig={{
                      autoResize: true,
                    }}
                  />
                </Grid>
                <Grid item>
                  <IgIconButton onClick={handleDialogClose}>
                    <X size={24} />
                  </IgIconButton>
                </Grid>
              </Grid>

              <Grid item>
                <IgTypography variant={"h1"}>You're ready to go!</IgTypography>
              </Grid>
              <Grid item sx={{ width: "400px" }}>
                <IgTypography color={theme.palette.text.secondary}>
                  We’ve confirmed that the script is on your store theme and
                  working as intended. You can close this window and start using
                  Intelligems.
                </IgTypography>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container item direction={"column"} wrap={"nowrap"}>
                <Grid
                  item
                  container
                  alignItems={"center"}
                  direction={"row"}
                  rowSpacing={1}
                  justifyContent={"space-between"}
                >
                  <Grid item>
                    <IgTypography variant={"h3"} fontWeight={600}>
                      {headerDetails.title}
                    </IgTypography>
                  </Grid>
                  <Grid item>
                    <IgIconButton size={"small"} onClick={handleDialogClose}>
                      <X size={24} />
                    </IgIconButton>
                  </Grid>
                </Grid>

                <Grid item sx={{ width: "550px" }}>
                  <IgTypography color={theme.palette.text.secondary}>
                    {headerDetails.subtitle}
                  </IgTypography>
                </Grid>
              </Grid>
              {step === 0 ? (
                <Grid mb={6} item container direction={"row"} columnSpacing={6}>
                  <Grid item>
                    <IgScriptTypeSelectCard
                      description={"The fastest way to enable Intelligems"}
                      title={"Enable app embeds"}
                      isRecommended={true}
                      selected={enableType === "embed"}
                      onSelect={() => {
                        setEnableType("embed");
                      }}
                      image={AppEmbedOnlyImage}
                    />
                  </Grid>
                  <Grid item>
                    <IgScriptTypeSelectCard
                      description={"For more control when you need it"}
                      title={"Add script to theme code"}
                      selected={enableType === "script"}
                      onSelect={() => {
                        setEnableType("script");
                      }}
                      image={ScriptOnlyImage}
                    />
                  </Grid>
                </Grid>
              ) : step === 1 ? (
                <Grid mb={6} item>
                  <StepCardsContainer steps={screenSteps} />
                </Grid>
              ) : (
                <Grid item mb={6}>
                  <IgTypography color={theme.palette.text.secondary}>
                    For more help, please{" "}
                    <IgTypography
                      onClick={() => {
                        openNewTab(
                          allIntegrationGuides,
                          "Adding Intelligems Script to your Theme"
                        );
                      }}
                      igStyle={"linkNoBold"}
                    >
                      visit our help center
                    </IgTypography>{" "}
                    or reach out to our support team{" "}
                    <IgTypography
                      onClick={contactSupport}
                      igStyle={"linkNoBold"}
                    >
                      here
                    </IgTypography>
                    .
                  </IgTypography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      }
    />
  );
};
